import { useForm } from '@tanstack/react-form'
import clsx from 'clsx'
import React from 'react'

import { useAmplitude } from '@Analytics/hook'
import { Drawer } from '@Components/Drawer'
import { DrawerPageSync } from '@Components/Drawer/Page'
import { Checkbox } from '@Components/FormElements/UI/Checkbox'
import { Button } from '@CoreComponents/Buttons/Button'
import { SizeGuideFitsContext } from '@Domains/Cart/Components/Prompt/Context'
import { DrawerDataFragment, PagePresentation } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'
import { getStore, useStore } from '@Globals/useStorage'

import styles from './Bracelet.module.scss'

export const store = getStore('wrist_size', '')

function range(start: number, end: number) { return Array.from({ length: end - start + 1 }, (_, i) => `${start + i}cm`) }

const BraceletWristSizeSelector = ({
  triggerRender,
}: {
  triggerRender?: React.ComponentProps<typeof Drawer>['triggerRender']
}) => {
  const wristSize = useStore(store)
  const [open, setOpen] = React.useState(false)
  const { onChangeSize } = React.use(SizeGuideFitsContext)
  const { t } = useTranslation()
  const ampli = useAmplitude()

  const form = useForm({
    defaultValues: { size: wristSize },
    onSubmit: ({ value }) => {
      store.setValue(value.size)
      onChangeSize()
      setOpen(false)
      ampli.measurementSelection({
        measurement_type: 'wrist_size',
        measurement_value: value.size,
      })
    },
  })

  const selectWristSizeText = t('cms:SELECT_SIZE', { context: 'wrist' })

  return (
    <DrawerPageSync
      footer={(
        <form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
          {([canSubmit, isSubmitting]) => (
            <Button
              disabled={!canSubmit}
              fillWidth
              innerSpacing="medium"
              loading={isSubmitting}
              onClick={() => form.handleSubmit()}
              theme="black"
              type="button"
            >
              {t('cms:COF_SELECT')}
            </Button>
          )}
        </form.Subscribe>
      )}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          form.reset({ size: store.getSnapshot() })
        }
        console.log('open change', nextOpen)
        setOpen(nextOpen)
      }}
      open={open}
      page={{
        id: 'bracelet_wrist_size_selector',
        title: selectWristSizeText,
        type: 'custom',
      } as DrawerDataFragment}
      presentation={PagePresentation.Sidebar}
      triggerRender={triggerRender || (props => (
        <Button
          {...props}
          className=""
          fillWidth
          innerSpacing="large"
          theme="black"
          type="button"
        >
          {selectWristSizeText}
          {wristSize && ` (${wristSize})`}
        </Button>
      ))}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
        style={{ display: 'contents' }}
      >
        <form.Field name="size">
          {field => (
            <>
              {range(13, 24).map(circumference => (
                <div key={circumference} className={clsx(styles.option, field.state.value === circumference && styles.selected)}>
                  <Checkbox
                    checked={field.state.value === circumference}
                    id={`wrist-size-${circumference}`}
                    label={circumference}
                    name={field.name}
                    onChange={() => field.handleChange(circumference)}
                    rounded
                  />
                </div>
              ))}
            </>
          )}
        </form.Field>
      </form>
    </DrawerPageSync>
  )
}

export default BraceletWristSizeSelector
